import { html, type TemplateResult } from 'lit-html';
import { api, ApiError, get, setConfig } from './api.js';
import { app, confirmDialog, isAdmin, mount, ROLE_LABEL, toast, type Me, type Page } from './app.js';
import { clearSession, completeSignIn, currentSession, OKTA_CALLBACK_PATH, signIn, signOut, type UiConfig } from './auth.js';
import { icon, type IconName } from './icons.js';
import { dashboard, health, loadHealth, modeBanner } from './pages/dashboard.js';
import { guide } from './pages/guide.js';
import { exemptions, users } from './pages/people.js';
import { queuesPage } from './pages/queues.js';
import { agentPage } from './pages/agent.js';
import { audit, reports } from './pages/reports.js';
import { rules } from './pages/rules.js';
import { timeouts } from './pages/timeouts.js';
import { setTheme, themePref, watchSystemTheme, type ThemePref } from './theme.js';

const ROUTES: Record<string, Page> = {
  dashboard,
  timeouts,
  queues: queuesPage,
  exemptions,
  rules,
  reports,
  agent: agentPage,
  audit,
  users,
  guide,
};

type Screen =
  | { kind: 'app' }
  | { kind: 'signin'; message?: string }
  | { kind: 'noaccess'; message: string }
  | { kind: 'unconfigured' }
  | { kind: 'error'; message: string };
let screen: Screen = { kind: 'app' };

function parseHash(): { route: string; param: string } {
  const [r = '', ...rest] = (location.hash.replace(/^#\/?/, '').split('?')[0] ?? '').split('/');
  return { route: ROUTES[r] ? r : 'dashboard', param: rest.join('/') };
}

async function navigate(): Promise<void> {
  const { route, param } = parseHash();
  // Moving between guide sections is a scroll, not a reload.
  if (route === app.route && route === 'guide') {
    app.routeParam = param;
    return app.rerender();
  }
  app.route = route;
  app.routeParam = param;
  const page = ROUTES[app.route]!;
  if (page.adminOnly && !isAdmin()) {
    location.hash = '#/dashboard';
    return;
  }
  app.error = null;
  if (page.instant) return app.rerender();
  app.loading = true;
  app.rerender();
  try {
    await page.load();
  } catch (err) {
    handleError(err);
  } finally {
    app.loading = false;
    app.rerender();
  }
}

function handleError(err: unknown): void {
  const e = err as ApiError;
  if (e.status === 401) {
    clearSession();
    screen = { kind: 'signin', message: 'Your session expired. Please sign in again.' };
  } else if (e.status === 403 && !app.me) {
    screen = { kind: 'noaccess', message: e.message };
  } else {
    app.error = e.message ?? String(err);
  }
  app.rerender();
}

async function emergencyStop(): Promise<void> {
  const c = await confirmDialog({
    title: 'Emergency stop',
    body: 'Switches the whole service to dry run within seconds. Nobody else will be deactivated until someone turns dry run off again on the Rules page. Agents already deactivated are not reactivated.',
    confirmLabel: 'Stop enforcing now',
    tone: 'danger',
    reasonField: true,
  });
  if (!c.ok) return;
  try {
    await api('POST', '/emergency-stop', { reason: c.reason || undefined });
    toast('Emergency stop applied: dry run everywhere.');
    await Promise.all([ROUTES[app.route]!.load(), loadHealth()]);
    app.rerender();
  } catch (err) {
    toast((err as Error).message, 'critical');
  }
}

function confirmModal(): TemplateResult | string {
  const c = app.confirm;
  if (!c) return '';
  const close = (ok: boolean) => {
    const reason = (document.getElementById('confirm-reason') as HTMLInputElement | null)?.value;
    app.confirm = null;
    c.resolve({ ok, reason });
    app.rerender();
  };
  return html`<div class="modal-backdrop" @click=${(e: Event) => e.target === e.currentTarget && close(false)}>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <h2 id="confirm-title">${c.title}</h2>
      <p>${c.body}</p>
      ${c.reasonField ? html`<label class="field">Reason (recorded in the audit log)<input id="confirm-reason" maxlength="500" placeholder="e.g. wrong queue enforced"></label>` : ''}
      <div class="actions">
        <button class="btn quiet" @click=${() => close(false)}>Cancel</button>
        <button class="btn ${c.tone === 'danger' ? 'destructive' : 'primary'}" @click=${() => close(true)}>${c.confirmLabel}</button>
      </div>
    </div></div>`;
}

const NAV: { route: string; label: string; icon: IconName; adminOnly?: boolean }[] = [
  { route: 'dashboard', label: 'Dashboard', icon: 'overview' },
  { route: 'timeouts', label: 'Timeouts', icon: 'timeouts' },
  { route: 'queues', label: 'Queues', icon: 'queues' },
  { route: 'exemptions', label: 'Exemptions', icon: 'exemptions' },
  { route: 'rules', label: 'Rules', icon: 'settings' },
  { route: 'reports', label: 'Reports', icon: 'reports' },
  { route: 'audit', label: 'Audit log', icon: 'logs' },
  { route: 'users', label: 'Users', icon: 'users', adminOnly: true },
  { route: 'guide', label: 'Guide', icon: 'guide' },
];

/** Below 1024px the rail is an overlay drawer; at desktop it's always shown. */
let drawerOpen = false;
let menuOpen = false;

function setDrawer(open: boolean): void {
  drawerOpen = open;
  app.rerender();
}
function setMenu(open: boolean): void {
  menuOpen = open;
  app.rerender();
}

function initials(name: string): string {
  const parts = (name.split('@')[0] ?? '').split(/[._\-\s]+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : (parts[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

/** Enforcement mode at a glance, on every page. */
function modePill(): TemplateResult | string {
  const s = health?.settings;
  if (!s?.loaded) return '';
  const [text, tone] =
    s.workerMode !== 'enforce'
      ? ['Observe only', 'info']
      : s.dryRun
        ? ['Dry run', 'info']
        : !s.allowLiveEnforcement
          ? ['Live switched off', 'warning']
          : ['Enforcing live', 'accent'];
  return html`<span class="pill ${tone} mode-pill" title="Enforcement mode">${text}</span>`;
}

const THEMES: { value: ThemePref; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Light theme', icon: 'sun' },
  { value: 'dark', label: 'Dark theme', icon: 'moon' },
  { value: 'system', label: 'Match system theme', icon: 'monitor' },
];

function themeSwitcher(): TemplateResult {
  const current = themePref();
  return html`<div class="theme-switcher" role="radiogroup" aria-label="Theme">
    ${THEMES.map((t) => html`<button type="button" role="radio" aria-checked=${current === t.value ? 'true' : 'false'} aria-label=${t.label}
      title=${t.label} class=${current === t.value ? 'active' : ''} @click=${() => { setTheme(t.value); app.rerender(); }}>${icon(t.icon)}</button>`)}
  </div>`;
}

function userMenu(): TemplateResult {
  const who = app.me.actor.replace(/^(sso|dev):/, '');
  const how = app.me.auth === 'sso' ? 'company sign-in' : app.me.auth === 'dev' ? 'local development' : 'AWS IAM';
  return html`<div class="user-wrap">
    <button type="button" class="user-trigger" aria-haspopup="menu" aria-expanded=${menuOpen ? 'true' : 'false'} @click=${() => setMenu(!menuOpen)}>
      <span class="avatar" aria-hidden="true">${initials(who)}</span>
      <span class="user-name">${who}</span>
      ${icon('chevron', 'chevron-down')}
    </button>
    ${menuOpen
      ? html`<div class="user-menu" role="menu" aria-label="Account">
          <div class="menu-header"><strong title=${app.me.actor}>${who}</strong><span>${ROLE_LABEL[app.me.role]} · ${how}</span></div>
          <div class="menu-divider"></div>
          <div class="menu-label">Theme</div>
          <div class="menu-row">${themeSwitcher()}</div>
          <div class="menu-divider"></div>
          <a class="menu-item" role="menuitem" href="#/guide/users" @click=${() => setMenu(false)}>${icon('guide')}What my role can do</a>
          ${app.config.auth === 'sso'
            ? html`<div class="menu-divider"></div>
              <button type="button" class="menu-item destructive" role="menuitem" @click=${() => { setMenu(false); signOut(app.config); }}>${icon('logout')}Sign out</button>`
            : ''}
        </div>`
      : ''}
  </div>`;
}

function shell(content: TemplateResult): TemplateResult {
  const items = NAV.filter((n) => !n.adminOnly || isAdmin());
  return html`
    <aside id="rail" class="rail ${drawerOpen ? 'open' : ''}" aria-label="Main navigation">
      <a class="rail-brand" href="#/dashboard" @click=${() => setDrawer(false)}>
        <span class="brand-logo brand-logo-light"><img class="brand-logo-light-art" src="assets/coverage/logo-white.png" alt="Catch Coverage"></span>
        <span class="rail-product">ACW Enforcer</span>
      </a>
      <nav class="rail-nav">${items.map((n) => {
        // An agent's report lives under Reports.
        const active = app.route === n.route || (n.route === 'reports' && app.route === 'agent');
        return html`<a href="#/${n.route}" class="nav-item ${active ? 'active' : ''}" aria-current=${active ? 'page' : 'false'}
          @click=${() => setDrawer(false)}>${icon(n.icon)}<span>${n.label}</span></a>`;
      })}</nav>
    </aside>
    ${drawerOpen ? html`<div class="scrim" @click=${() => setDrawer(false)}></div>` : ''}
    <div class="main">
      <header class="topbar">
        <div class="topbar-left">
          <button type="button" class="btn quiet compact icon-only menu-btn" aria-label="Open navigation" aria-controls="rail"
            aria-expanded=${drawerOpen ? 'true' : 'false'} @click=${() => setDrawer(!drawerOpen)}>${icon('menu')}</button>
          ${modePill()}
        </div>
        <div class="topbar-right">
          ${isAdmin() ? html`<button type="button" class="btn destructive outline compact" @click=${emergencyStop}>Emergency stop</button>` : ''}
          ${userMenu()}
        </div>
      </header>
      <main class="content">
        ${app.route !== 'dashboard' && app.route !== 'guide' ? modeBanner(health) : ''}
        ${app.error ? html`<div class="banner critical">${app.error} <button class="btn quiet compact" @click=${navigate}>Retry</button></div>` : ''}
        ${app.loading ? html`<div class="loading" aria-live="polite"><span class="spinner" aria-hidden="true"></span>Loading…</div>` : ''}
        ${content}
      </main>
    </div>
    <div class="toasts" aria-live="polite">${app.toasts.map((t) => html`<div class="toast ${t.tone}">${t.text}</div>`)}</div>
    ${confirmModal()}`;
}

function centered(title: string, body: TemplateResult | string, action?: TemplateResult): TemplateResult {
  return html`<div class="center"><div class="card signin">
    <span class="brand-logo brand-logo-auto">
      <img class="brand-logo-dark-art" src="assets/coverage/logo-black.png" alt="Catch Coverage">
      <img class="brand-logo-light-art" src="assets/coverage/logo-white.png" alt="Catch Coverage">
    </span>
    <h1>ACW Enforcer</h1><p>${body}</p>${action ?? ''}<p class="muted">${title}</p></div></div>`;
}

function view(): TemplateResult {
  switch (screen.kind) {
    case 'signin':
      return centered(
        'Sign in with your company account.',
        screen.message ?? 'Manage ACW timeout enforcement: queues, rules, exemptions and reports.',
        html`<button class="btn primary" @click=${() => signIn(app.config).catch((e: Error) => { screen = { kind: 'error', message: e.message }; app.rerender(); })}>Sign in with Okta</button>`,
      );
    case 'noaccess':
      return centered('Access is managed by ACW Enforcer admins.', screen.message, html`<button class="btn secondary" @click=${() => signOut(app.config)}>Sign out</button>`);
    case 'unconfigured':
      return centered('Ask the ACW Enforcer system owner to finish the Okta setup.', "Company sign-in isn't set up for this environment yet, so nobody can sign in here.");
    case 'error':
      return centered('Something went wrong.', screen.message, html`<button class="btn secondary" @click=${() => location.assign('/ui/')}>Start again</button>`);
    case 'app':
      return shell(ROUTES[app.route]!.view());
  }
}

async function boot(): Promise<void> {
  // The app positions the page itself (guide deep links); don't let the browser restore old offsets.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  mount(document.getElementById('app')!, view);
  const config = await fetch('/ui/config.json').then((r) => r.json() as Promise<UiConfig>);
  app.config = config;
  setConfig(config);

  if (config.auth === 'unconfigured') {
    screen = { kind: 'unconfigured' };
    return app.rerender();
  }
  if (config.auth === 'sso') {
    if (location.pathname === OKTA_CALLBACK_PATH) {
      try {
        const returnTo = await completeSignIn(config);
        history.replaceState(null, '', `/ui/${returnTo}`);
      } catch (err) {
        screen = { kind: 'error', message: (err as Error).message };
        return app.rerender();
      }
    }
    if (!currentSession()) {
      screen = { kind: 'signin' };
      return app.rerender();
    }
  }
  try {
    app.me = await get<Me>('/me');
  } catch (err) {
    return handleError(err);
  }
  screen = { kind: 'app' };
  // Menus close on outside click and Escape; the drawer closes on Escape and on navigation.
  document.addEventListener('click', (e) => {
    if (menuOpen && !(e.target as Element).closest('.user-wrap')) setMenu(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (menuOpen) setMenu(false);
    if (drawerOpen) setDrawer(false);
  });
  watchSystemTheme(() => app.rerender());
  // The top bar's mode pill needs health on every page, not just the ones that load it.
  void loadHealth().then(() => app.rerender(), () => undefined);
  window.addEventListener('hashchange', () => void navigate());
  await navigate();
}

void boot();
